import React from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMoney } from "../utils";

/**
 * Inline panel shown under the SubmissionDetailPage header when the lead
 * has been marked ``won``. Nudges reps into recording the final won value
 * plus a close date so revenue reporting stays accurate.
 *
 * Soft requirement — the caller (endpoint) does not block on missing
 * ``final_won_value``, this UI just makes it easy to fix in one click.
 *
 * Props:
 *   - doc:      the submission document (uses ``final_won_value``,
 *               ``close_date``, ``estimated_value``)
 *   - onSave:   ``(valueString, closeDateIso | undefined) => Promise<void>``
 *   - disabled: hides the edit/save actions when the viewer is read-only
 *   - busy:     network activity flag, disables the buttons while pending
 *
 * Extracted from SubmissionDetailPage.jsx in the Feb 2026 refactor to bring
 * that file back under 600 lines and give the panel its own testing surface.
 */
export default function WonValuePanel({ doc, onSave, disabled, busy }) {
  const [editing, setEditing] = React.useState(!doc.final_won_value);
  const [value, setValue] = React.useState(doc.final_won_value ? String(doc.final_won_value) : "");
  const [dateStr, setDateStr] = React.useState(() => {
    const iso = doc.close_date;
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    } catch { return ""; }
  });

  const save = async () => {
    if (!value) return;
    const iso = dateStr ? new Date(dateStr).toISOString() : undefined;
    await onSave(value, iso);
    setEditing(false);
  };

  return (
    <div
      className="mt-4 flex items-start gap-3 p-3 rounded-md bg-emerald-50 border border-emerald-200"
      data-testid="won-value-panel"
    >
      <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
      <div className="flex-1">
        {!editing ? (
          <div className="flex items-center justify-between gap-3 flex-wrap text-sm">
            <div>
              <span className="font-medium text-emerald-900">Won value:</span>{" "}
              <span className="text-emerald-900">{formatMoney(doc.final_won_value)}</span>
              {doc.close_date && (
                <span className="text-emerald-700 text-xs ml-2">
                  closed {new Date(doc.close_date).toLocaleDateString()}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={disabled || busy}
              className="text-xs font-medium text-emerald-800 hover:underline disabled:opacity-50"
              data-testid="won-value-edit-btn"
            >
              Edit
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-emerald-900">
              Record the final won value so reporting stays accurate.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 bg-white rounded-md border border-emerald-200 px-2 py-1">
                <span className="text-emerald-700 text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  placeholder={String(doc.estimated_value || 0)}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-32 text-sm outline-none bg-transparent"
                  data-testid="won-value-input"
                />
              </div>
              <input
                type="date"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                className="text-sm border border-emerald-200 rounded-md px-2 py-1 bg-white"
                data-testid="won-close-date-input"
              />
              <Button
                type="button"
                size="sm"
                onClick={save}
                disabled={!value || disabled || busy}
                data-testid="won-value-save-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Save
              </Button>
              {doc.final_won_value && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(false)}
                  className="text-emerald-800"
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
